import React from "react"
import { SxProps, Theme } from "@mui/system"
import { C, radius, transition } from "@/theme/newDesignTokens"

// ── Toolbar layout ──────────────────────────────────────────────────────────

export const toolbarContainer: React.CSSProperties = {
    display: "flex",
    width: "100%",
    minWidth: 0,
    height: 44,
    padding: "0 12px",
    alignItems: "center",
    gap: 8,
    background: C.white,
    borderBottom: `1px solid ${C.lightGray}`,
}

export const leftGroup: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    flex: 1,
}

export const centerGroup: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "14rem",
    flexShrink: 0,
    overflow: "hidden",
    boxSizing: "border-box",
}

export const rightGroup: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
    minWidth: 0,
}

export const toolbarDivider: React.CSSProperties = {
    width: 1,
    height: 20,
    background: C.lightGray,
    flexShrink: 0,
}

// ── Filename ────────────────────────────────────────────────────────────────

export const getCustomLinkStyle = (isSmallScreen: boolean): React.CSSProperties => ({
    lineHeight: "1.3",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
    minWidth: 0,
})

export const fileNameSx: SxProps<Theme> = {
    fontSize: 13,
    fontWeight: 600,
    color: C.navyDark,
    transition: transition.fast,
    "&:hover": {
        color: C.navy,
    },
}

// ── Page pill ───────────────────────────────────────────────────────────────

export const pagePill: React.CSSProperties = {
    background: C.offWhite,
    borderRadius: radius.lg,
    padding: "3px 10px",
    fontSize: 11,
    fontWeight: 500,
    color: C.medGray,
    whiteSpace: "nowrap",
    flexShrink: 0,
}

// ── Search ──────────────────────────────────────────────────────────────────

export const getSearchWrapper = (isFocused: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    gap: 6,
    height: 30,
    padding: "0 8px",
    background: isFocused ? C.white : C.offWhite,
    border: isFocused ? `1px solid ${C.periwinkle}` : "1px solid transparent",
    borderRadius: radius.md,
    transition: transition.fast,
    boxSizing: "border-box",
})

export const searchIcon: React.CSSProperties = {
    flexShrink: 0,
    color: C.medGray,
}

export const searchInput: React.CSSProperties = {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 12,
    color: C.navyDark,
    flex: 1,
    minWidth: 0,
    height: "100%",
    fontFamily: "inherit",
}

export const matchCount: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    color: C.medGray,
    whiteSpace: "nowrap",
    padding: "0 4px",
    flexShrink: 0,
    maxWidth: 56,
    overflow: "hidden",
    textOverflow: "ellipsis",
}

export const notFoundText: React.CSSProperties = {
    fontSize: 11,
    color: C.coral,
    whiteSpace: "nowrap",
    padding: "0 4px",
    flexShrink: 0,
    maxWidth: 56,
    overflow: "hidden",
    textOverflow: "ellipsis",
}

export const navButton: React.CSSProperties = {
    width: 22,
    height: 22,
    border: "none",
    borderRadius: radius.sm,
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: C.medGray,
    transition: transition.fast,
    padding: 0,
}

export const navButtonHovered: React.CSSProperties = {
    background: C.offWhite,
    color: C.navyDark,
}

// ── Zoom ────────────────────────────────────────────────────────────────────

export const zoomGroup: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    background: C.offWhite,
    borderRadius: radius.md,
    padding: "0 2px",
    gap: 0,
    flexShrink: 0,
    height: 30,
}

export const zoomButton: React.CSSProperties = {
    width: 28,
    height: 28,
    border: "none",
    borderRadius: radius.sm,
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: C.medGray,
    transition: transition.fast,
    padding: 0,
}

export const zoomMiddle: React.CSSProperties = {
    padding: 0,
}

// ── Toolbar icon buttons ────────────────────────────────────────────────────

export const toolbarIconBtn: SxProps<Theme> = {
    width: 30,
    height: 30,
    borderRadius: `${radius.md}px`,
    color: C.medGray,
    transition: transition.fast,
    "&:hover": {
        background: C.offWhite,
        color: C.navyDark,
    },
}

export const toolbarIconBtnClose: SxProps<Theme> = {
    width: 30,
    height: 30,
    borderRadius: `${radius.md}px`,
    color: C.coral,
    transition: transition.fast,
    "&:hover": {
        background: `${C.coral}10`,
        color: C.coral,
    },
}

// ── Viewer container ────────────────────────────────────────────────────────

export const getViewerContainerSx = (height: string): SxProps<Theme> => ({
    position: "relative",
    height: height,
    background: C.white,
})

export const getViewerInnerSx = (height: string): SxProps<Theme> => ({
    height: height,
    overflowY: "none",
    overflowX: "hidden",
})
